import { useMemo, useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { AlertTriangle, Car, Download } from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  ErrorState,
  SOFT_SURFACE_CLASSES,
  SOFT_TEXT_CLASSES,
  SkeletonTable,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
} from '@mustauto/ui/web';
import { maskPhone } from '@mustauto/utils';
import { compareInShopByDeadline, formatDayCount, type InShopColumn } from '@mustauto/types/in-shop';
import { useAuth } from '@/hooks/useAuth';
import { useOSRealtime } from '@/hooks/useOSRealtime';
import { useCarsInShop, useInShopView, type InShopEntry, type InShopLocation } from '@/hooks/useCarsInShop';
import { Badge } from '@/components/ui/badge';
import { STATUS_MAP } from '@/lib/status';
import { useCan } from '@/hooks/useCan';
import { DeliverOSButton } from '@/components/DeliverOSButton';
import { WhoCanHint } from '@/components/WhoCanHint';
import { InShopDeadline } from '@/components/cars-in-shop/InShopDeadline';
import { InShopColumnsPicker } from '@/components/cars-in-shop/InShopColumnsPicker';
import { InShopOrderToggle } from '@/components/cars-in-shop/InShopOrderToggle';
import { InShopExportDialog } from '@/components/cars-in-shop/InShopExportDialog';

/** Ordem e nome dos grupos — a mesma da API, do PDF e do CSV. */
const GROUPS: { key: InShopLocation; title: string; hint?: string }[] = [
  { key: 'bay', title: 'Em vaga', hint: 'Para trocar a vaga, abra a OS.' },
  { key: 'paint_booth', title: 'Cabine de pintura' },
  { key: 'no_slot', title: 'Sem vaga definida', hint: 'OS aberta sem escolher vaga.' },
  {
    key: 'outsourced',
    title: 'Com terceiro',
    hint: 'O serviço está com um terceiro — às vezes vai o carro, às vezes só a peça.',
  },
  {
    key: 'ready',
    title: 'Pronto, aguardando retirada',
    hint: 'Concluídas e ainda não marcadas como entregues. Quando o cliente buscar, marque a entrega e o carro sai da lista.',
  },
];

function daysLabel(days: number): string {
  return days === 0 ? 'Hoje' : formatDayCount(days);
}

/** Cabeçalho e célula de cada coluna escolhível. "Veículo e cor" não é coluna: é a linha sob a placa. */
const COLUMN_CELLS: Record<
  Exclude<InShopColumn, 'vehicle'>,
  { head: string; right?: boolean; render: (v: InShopEntry) => ReactNode }
> = {
  customer: { head: 'Cliente', render: (v) => v.customerName ?? '—' },
  customerPhone: {
    head: 'Telefone',
    render: (v) => <span className="whitespace-nowrap">{v.customerPhone ? maskPhone(v.customerPhone) : '—'}</span>,
  },
  os: {
    head: 'OS',
    render: (v) => (
      <Link to="/service-orders/$osId" params={{ osId: v.osId }} className="font-medium text-primary hover:underline">
        #{v.number}
      </Link>
    ),
  },
  status: {
    head: 'Status',
    render: (v) => {
      const status = STATUS_MAP[v.status];
      return (
        <>
          <Badge variant={status?.variant ?? 'outline'}>{status?.label ?? v.status}</Badge>
          {v.stageLabel && <p className="mt-1 text-xs text-muted-foreground">{v.stageLabel}</p>}
        </>
      );
    },
  },
  deadline: { head: 'Prazo', render: (v) => <InShopDeadline entry={v} /> },
  providers: {
    head: 'Terceiros',
    render: (v) => <span className="text-sm">{v.providers.length > 0 ? v.providers.join(', ') : '—'}</span>,
  },
  mechanic: { head: 'Mecânico', render: (v) => v.mechanicName ?? '—' },
  enteredAt: {
    head: 'Entrou em',
    render: (v) => <span className="tabular-nums">{new Date(v.enteredAt).toLocaleDateString('pt-BR')}</span>,
  },
  daysInShop: { head: 'Na oficina há', right: true, render: (v) => daysLabel(v.daysInShop) },
};

function CarsTable({
  entries,
  columns,
  location,
}: {
  entries: InShopEntry[];
  columns: readonly InShopColumn[];
  /** `slot`: a vaga (grupo Em vaga). `place`: onde está (ordem por prazo, sem grupos). */
  location: 'slot' | 'place' | null;
}) {
  const showVehicle = columns.includes('vehicle');
  const cells = columns.filter((c): c is Exclude<InShopColumn, 'vehicle'> => c !== 'vehicle');
  // O carro pronto foi embora? Entrega daqui, em qualquer ordem.
  const showDeliver = entries.some((v) => v.location === 'ready');

  return (
    <Table>
      <TableHeader>
        <TableRow>
          {location && <TableHead>{location === 'slot' ? 'Vaga' : 'Onde está'}</TableHead>}
          <TableHead>Carro</TableHead>
          {cells.map((c) => (
            <TableHead key={c} className={COLUMN_CELLS[c].right ? 'text-right' : undefined}>
              {COLUMN_CELLS[c].head}
            </TableHead>
          ))}
          {showDeliver && (
            <TableHead className="w-0">
              <span className="sr-only">Entrega</span>
            </TableHead>
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {entries.map((v) => (
          <TableRow key={v.osId}>
            {location && (
              <TableCell
                className={
                  location === 'slot' || v.location === 'bay' || v.location === 'paint_booth'
                    ? 'font-semibold'
                    : 'text-muted-foreground'
                }
              >
                {v.locationLabel}
              </TableCell>
            )}
            <TableCell>
              <p className="font-mono font-bold">{v.plate}</p>
              {showVehicle && (
                <p className="text-xs text-muted-foreground">{[v.vehicleName, v.color].filter(Boolean).join(' · ')}</p>
              )}
            </TableCell>
            {cells.map((c) => (
              <TableCell key={c} className={COLUMN_CELLS[c].right ? 'text-right tabular-nums' : undefined}>
                {COLUMN_CELLS[c].render(v)}
              </TableCell>
            ))}
            {showDeliver && (
              <TableCell className="text-right">
                {v.location === 'ready' && (
                  <DeliverOSButton osId={v.osId} osNumber={v.number} plate={v.plate} compact />
                )}
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** Aviso do topo: quem passou do prazo e quem vence hoje, com atalho para a ordem por prazo. */
function DeadlineAlert({
  lateCount,
  dueTodayCount,
  onShowByDeadline,
}: {
  lateCount: number;
  dueTodayCount: number;
  onShowByDeadline: (() => void) | null;
}) {
  if (lateCount === 0 && dueTodayCount === 0) return null;
  const today = dueTodayCount > 0 ? plural(dueTodayCount, 'vence hoje', 'vencem hoje') : null;
  const color = lateCount > 0 ? 'red' : 'amber';
  return (
    <div
      role="status"
      className={cn(
        'flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border px-4 py-2.5 text-sm',
        SOFT_SURFACE_CLASSES[color],
        SOFT_TEXT_CLASSES[color],
      )}
    >
      <AlertTriangle size={16} className="shrink-0" />
      <span>
        {lateCount > 0 ? (
          <>
            <strong>{plural(lateCount, 'carro passou do prazo', 'carros passaram do prazo')}</strong>
            {today && ` · ${today}`}
          </>
        ) : (
          <strong>{plural(dueTodayCount, 'carro vence hoje', 'carros vencem hoje')}</strong>
        )}
      </span>
      {onShowByDeadline && (
        <button type="button" className="ml-auto font-semibold underline" onClick={onShowByDeadline}>
          Ver por prazo
        </button>
      )}
    </div>
  );
}

export function CarsInShopPage() {
  const { user } = useAuth();
  const can = useCan();
  const canExport = can('clientes.exportar');
  const canDeliver = can('os.entregar');
  const workshopId = user?.workshopId ?? null;
  const { data, isLoading, isError, error, refetch } = useCarsInShop(workshopId);
  const { columns, setColumns, order, setOrder } = useInShopView();
  const [exportOpen, setExportOpen] = useState(false);

  useOSRealtime(workshopId);

  const groups = useMemo(() => {
    const vehicles = data?.vehicles ?? [];
    return GROUPS.map((g) => {
      const entries = vehicles.filter((v) => v.location === g.key);
      return { ...g, entries, late: entries.filter((v) => v.deadlineStatus === 'late').length };
    }).filter((g) => g.entries.length > 0);
  }, [data]);

  const byDeadline = useMemo(() => [...(data?.vehicles ?? [])].sort(compareInShopByDeadline), [data]);

  const total = data?.vehicles.length ?? 0;
  const updatedAt = data
    ? new Date(data.generatedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Carros na oficina</h1>
          <p className="text-sm text-muted-foreground">
            {data
              ? `${total} ${total === 1 ? 'carro' : 'carros'} agora · atualizado às ${updatedAt}`
              : 'Todos os carros que estão na oficina e onde cada um está'}
          </p>
        </div>
        {canExport && (
          <button
            type="button"
            onClick={() => setExportOpen(true)}
            disabled={!data}
            className="flex items-center gap-2 self-start rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60 sm:self-auto"
          >
            <Download size={16} />
            Exportar
          </button>
        )}
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="pt-6">
            <SkeletonTable rows={6} />
          </CardContent>
        </Card>
      ) : isError && !data ? (
        <ErrorState title="Não foi possível carregar os carros na oficina" error={error} onRetry={() => refetch()} />
      ) : total === 0 ? (
        <EmptyState
          icon={<Car size={32} />}
          title="Nenhum carro na oficina agora"
          description="Os carros aparecem aqui assim que a OS é aberta, e saem quando ela é entregue ou cancelada."
        />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <InShopColumnsPicker columns={columns} onChange={setColumns} />
            <span className="text-sm text-muted-foreground">Ordenar por</span>
            <InShopOrderToggle order={order} onChange={setOrder} />
          </div>

          {data && (
            <DeadlineAlert
              lateCount={data.lateCount}
              dueTodayCount={data.dueTodayCount}
              onShowByDeadline={order === 'location' ? () => setOrder('deadline') : null}
            />
          )}

          {order === 'deadline' ? (
            <Card>
              <CardContent className="overflow-x-auto pt-2">
                <CarsTable entries={byDeadline} columns={columns} location="place" />
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                {groups.map((g) => (
                  <a
                    key={g.key}
                    href={`#grupo-${g.key}`}
                    className="rounded-lg border border-border bg-card px-4 py-3 hover:bg-muted"
                  >
                    <p className="text-2xl font-bold tabular-nums">{g.entries.length}</p>
                    <p className="text-sm text-muted-foreground">{g.title}</p>
                    {g.late > 0 && (
                      <p className={cn('text-xs font-semibold', SOFT_TEXT_CLASSES.red)}>
                        {plural(g.late, 'atrasado', 'atrasados')}
                      </p>
                    )}
                  </a>
                ))}
              </div>

              {groups.map((g) => (
                <Card key={g.key} id={`grupo-${g.key}`}>
                  <CardHeader>
                    <CardTitle className="text-base">
                      {g.title} ({g.entries.length})
                    </CardTitle>
                    {g.hint && <p className="text-xs text-muted-foreground">{g.hint}</p>}
                    {g.key === 'ready' && !canDeliver && (
                      <WhoCanHint permission="os.entregar" action="entrega o veículo" />
                    )}
                  </CardHeader>
                  <CardContent className="overflow-x-auto">
                    <CarsTable entries={g.entries} columns={columns} location={g.key === 'bay' ? 'slot' : null} />
                  </CardContent>
                </Card>
              ))}
            </>
          )}
        </>
      )}

      {data && data.olderReadyCount > 0 && (
        <p className="text-sm text-muted-foreground">
          {data.olderReadyCount === 1
            ? `1 OS concluída há mais de ${data.readyWindowDays} dias não aparece aqui porque não foi marcada como entregue.`
            : `${data.olderReadyCount} OS concluídas há mais de ${data.readyWindowDays} dias não aparecem aqui porque não foram marcadas como entregues.`}{' '}
          <Link to="/service-orders" search={{ status: 'completed' }} className="font-medium text-primary hover:underline">
            Ver concluídas
          </Link>
        </p>
      )}

      {canExport && data && (
        <InShopExportDialog
          open={exportOpen}
          onClose={() => setExportOpen(false)}
          vehicles={data.vehicles}
          groups={groups.map((g) => ({ key: g.key, title: g.title, count: g.entries.length }))}
          columns={columns}
          onColumnsChange={setColumns}
          order={order}
          onOrderChange={setOrder}
        />
      )}
    </div>
  );
}
